import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Button,
  Text,
  Badge,
  Tabs,
  FilterChips,
  SearchInput,
  EmptyState,
  StatusDot,
} from "@teals/ui-web";
import type { TabItem } from "@teals/ui-web";
import {
  useGetDataSourcesQuery,
  useUpdateAgentAssociationsMutation,
  type DataSource,
  type DsType,
} from "../../store/api/dataSourcesApi";
import { useGetMcpIntegrationsQuery } from "../../store/api/mcpIntegrationsApi";
import { useGetAgentsQuery } from "../../store/api/agentsApi";
import { useAuth } from "../../hooks/useAuth";
import {
  DS_TYPE_LABELS,
  DS_TYPE_DESCRIPTIONS,
  DS_TYPE_BADGE_VARIANT,
} from "../DataSources/constants";
import {
  PageWrapper,
  Header,
  HeaderRow,
  Subtitle,
  Grid,
  ErrorBanner,
} from "../DataSources/styles";
import DataSourceCard from "../DataSources/DataSourceCard";
import CreateWizard from "../DataSources/CreateWizard/CreateWizard";
import CustomMcpCard from "./CustomMcpCard";
import CustomMcpWizard from "./CustomMcpWizard";
import { API_BASE_URL } from "../../utils/api";
import {
  HubStats,
  HubStat,
  ActionBar,
  ActionBarSpacer,
  CatalogGroup,
  CatalogGroupTitle,
  CatalogTile,
  CatalogTileHead,
  CatalogTileTitle,
  CatalogTileDesc,
  CatalogTileFoot,
  CatalogMeta,
} from "./styles";
import {
  JIRA_PATH,
  XERO_PATH,
  dataSourceInstancePath,
  mcpInstancePath,
  isDsType,
  type HubCategory,
} from "./paths";

function agentIdFromReturnTo(returnTo: string | null): string | null {
  if (!returnTo) return null;
  const match = returnTo.match(/\/agents\/([^/?#]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

const HUB_TABS: TabItem[] = [
  { id: "connected", label: "Connected" },
  { id: "catalog", label: "Catalog" },
];

const DS_CATALOG: {
  type: DsType;
  description: string;
  kind: "Retrieval";
}[] = [
  {
    type: "search_index",
    description: `${DS_TYPE_DESCRIPTIONS.search_index}. Platform-managed.`,
    kind: "Retrieval",
  },
  {
    type: "blob_storage",
    description: `${DS_TYPE_DESCRIPTIONS.blob_storage}. Platform-managed.`,
    kind: "Retrieval",
  },
  {
    type: "unstructured_store",
    description: `${DS_TYPE_DESCRIPTIONS.unstructured_store}. Platform-managed.`,
    kind: "Retrieval",
  },
  {
    type: "byo",
    description:
      "Link an existing Algolia index you already manage. Records stay in your account.",
    kind: "Retrieval",
  },
];

const TOOL_CATALOG = [
  {
    id: "jira",
    name: "Jira",
    description:
      "Let agents search issues, projects, and comments. Connect OAuth when this ships.",
    to: JIRA_PATH,
  },
  {
    id: "xero",
    name: "Xero",
    description:
      "Let agents query invoices, contacts, and accounts. Connect OAuth when this ships.",
    to: XERO_PATH,
  },
] as const;

const IntegrationsHub: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgId = user?.organizationId || "";
  const [searchParams, setSearchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const openCreate = searchParams.get("create") === "1";
  const openMcpCreate = searchParams.get("create") === "custom_mcp";
  const returnTab = searchParams.get("tab");
  const typeParam = searchParams.get("type");
  const categoryParam = searchParams.get("category");
  const category: HubCategory =
    categoryParam === "data_source" || categoryParam === "tool"
      ? categoryParam
      : "all";

  const { data: dataSources, isLoading, error } = useGetDataSourcesQuery(
    { orgId },
    { skip: !orgId },
  );
  const { data: agents = [] } = useGetAgentsQuery(
    { orgId },
    { skip: !orgId },
  );
  const [updateAgentAssociations] = useUpdateAgentAssociationsMutation();

  const { data: mcpIntegrations } = useGetMcpIntegrationsQuery(
    { orgId },
    { skip: !orgId },
  );

  const [search, setSearch] = useState("");
  const [hubTab, setHubTab] = useState<"connected" | "catalog">(
    category === "tool" ? "catalog" : "connected",
  );
  const [wizardOpen, setWizardOpen] = useState(openCreate);
  const [wizardType, setWizardType] = useState<DsType | undefined>(
    isDsType(typeParam) ? typeParam : undefined,
  );
  const [mcpWizardOpen, setMcpWizardOpen] = useState(openMcpCreate);

  useEffect(() => {
    if (openCreate) setWizardOpen(true);
  }, [openCreate]);

  useEffect(() => {
    if (openMcpCreate) setMcpWizardOpen(true);
  }, [openMcpCreate]);

  useEffect(() => {
    if (isDsType(typeParam)) setWizardType(typeParam);
  }, [typeParam]);

  useEffect(() => {
    if (category === "tool") setHubTab("catalog");
  }, [category]);

  const connectedSources = useMemo(() => {
    if (!dataSources) return [];
    return dataSources.filter((ds) => {
      const matchesSearch =
        !search ||
        ds.name.toLowerCase().includes(search.toLowerCase()) ||
        ds.dataSourceId.toLowerCase().includes(search.toLowerCase()) ||
        DS_TYPE_LABELS[ds.dsType].toLowerCase().includes(search.toLowerCase());
      const matchesCategory =
        category === "all" || category === "data_source";
      return matchesSearch && matchesCategory;
    });
  }, [dataSources, search, category]);

  const connectedMcps = useMemo(() => {
    if (!mcpIntegrations) return [];
    const q = search.toLowerCase();
    return mcpIntegrations.filter((mcp) => {
      const matchesSearch =
        !q ||
        mcp.name.toLowerCase().includes(q) ||
        mcp.mcpId.toLowerCase().includes(q) ||
        (mcp.serverUrl || "").toLowerCase().includes(q);
      const matchesCategory = category === "all" || category === "tool";
      return matchesSearch && matchesCategory;
    });
  }, [mcpIntegrations, search, category]);

  const agentNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const agent of agents) {
      if (agent.id) names[agent.id] = agent.name || agent.agentName;
      if (agent.agentName) names[agent.agentName] = agent.name || agent.agentName;
    }
    return names;
  }, [agents]);

  const catalogQuery = search.toLowerCase();
  const showDataSourceCatalog =
    category !== "tool" &&
    DS_CATALOG.some(
      (tile) =>
        !catalogQuery ||
        DS_TYPE_LABELS[tile.type].toLowerCase().includes(catalogQuery) ||
        tile.description.toLowerCase().includes(catalogQuery),
    );
  const visibleDsCatalog = DS_CATALOG.filter(
    (tile) =>
      !catalogQuery ||
      DS_TYPE_LABELS[tile.type].toLowerCase().includes(catalogQuery) ||
      tile.description.toLowerCase().includes(catalogQuery),
  );
  const visibleTools = TOOL_CATALOG.filter(
    (tile) =>
      category !== "data_source" &&
      (!catalogQuery ||
        tile.name.toLowerCase().includes(catalogQuery) ||
        tile.description.toLowerCase().includes(catalogQuery)),
  );

  const setCategory = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === "all") next.delete("category");
    else next.set("category", id);
    setSearchParams(next, { replace: true });
  };

  const openWizard = (type?: DsType) => {
    setWizardType(type);
    setWizardOpen(true);
    const next = new URLSearchParams(searchParams);
    next.set("create", "1");
    if (type) next.set("type", type);
    else next.delete("type");
    setSearchParams(next, { replace: true });
  };

  const clearCreateParams = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("create");
    next.delete("type");
    setSearchParams(next, { replace: true });
  };

  const openMcpWizard = () => {
    setMcpWizardOpen(true);
    const next = new URLSearchParams(searchParams);
    next.set("create", "custom_mcp");
    next.delete("type");
    setSearchParams(next, { replace: true });
  };

  const navigateBackToAgent = async (ds?: DataSource) => {
    if (!returnTo) return false;
    const agentId = agentIdFromReturnTo(returnTo);
    if (agentId && ds) {
      try {
        const nextIds = ds.agentIds.includes(agentId)
          ? ds.agentIds
          : [...ds.agentIds, agentId];
        await updateAgentAssociations({
          id: ds.dataSourceId,
          agentIds: nextIds,
        }).unwrap();
      } catch {
        /* association may fail if DS not yet linkable; still return */
      }
    }
    const target =
      returnTab && !returnTo.includes("tab=")
        ? `${returnTo}${returnTo.includes("?") ? "&" : "?"}tab=${encodeURIComponent(returnTab)}`
        : returnTo;
    navigate(target);
    return true;
  };

  const connectedCount = dataSources?.length ?? 0;
  const mcpCount = mcpIntegrations?.length ?? 0;
  const emptyBecauseFilter =
    Boolean(dataSources?.length || mcpIntegrations?.length) &&
    connectedSources.length === 0 &&
    connectedMcps.length === 0;
  const showConnectedDs = category !== "tool";
  const showConnectedMcp = category !== "data_source";
  const connectedEmpty =
    (showConnectedDs ? connectedSources.length === 0 : true) &&
    (showConnectedMcp ? connectedMcps.length === 0 : true);

  return (
    <PageWrapper>
      <Header>
        <HeaderRow>
          <Text variant="h2">Integrations</Text>
        </HeaderRow>
        <Subtitle>
          Connect data sources and tools your agents can use. Each connection
          has its own page.
        </Subtitle>
        <HubStats>
          <HubStat>
            <StatusDot status="active" size="sm" />
            <span>
              <strong>{connectedCount}</strong> data source
              {connectedCount === 1 ? "" : "s"} connected
            </span>
          </HubStat>
          <HubStat>
            <StatusDot status="active" size="sm" />
            <span>
              <strong>{mcpCount}</strong> Custom MCP
              {mcpCount === 1 ? "" : "s"}
            </span>
          </HubStat>
          <HubStat>Jira and Xero coming soon</HubStat>
        </HubStats>
      </Header>

      <ActionBar>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search integrations…"
          debounceMs={0}
          style={{ flex: 1, maxWidth: 280 }}
        />
        <FilterChips
          items={[
            { id: "all", label: "All" },
            { id: "data_source", label: "Data sources" },
            { id: "tool", label: "Tools" },
          ]}
          activeId={category}
          onChange={setCategory}
        />
        <ActionBarSpacer />
        <Button variant="primary" onClick={() => openWizard()}>
          + Add integration
        </Button>
      </ActionBar>

      <Tabs
        items={HUB_TABS}
        activeTab={hubTab}
        onChange={(id) => {
          if (id === "connected" || id === "catalog") setHubTab(id);
        }}
      />

      {error && (
        <ErrorBanner>
          Failed to load data sources. Please try again.
        </ErrorBanner>
      )}

      {hubTab === "connected" && (
        <div style={{ marginTop: "1.25rem" }}>
          {isLoading ? (
            <EmptyState title="Loading integrations…" />
          ) : connectedEmpty ? (
            <EmptyState
              title={
                emptyBecauseFilter
                  ? "No connections in this filter."
                  : "No connections yet."
              }
              description={
                emptyBecauseFilter
                  ? undefined
                  : "Create a data source or add a Custom MCP from the catalog."
              }
              action={
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  {!emptyBecauseFilter && (
                    <>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => openWizard()}
                      >
                        Create a data source
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={openMcpWizard}
                      >
                        Add Custom MCP
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setHubTab("catalog")}
                  >
                    Browse catalog
                  </Button>
                </div>
              }
            />
          ) : (
            <Grid>
              {showConnectedDs &&
                connectedSources.map((ds) => (
                  <DataSourceCard
                    key={ds.dataSourceId}
                    ds={ds}
                    to={dataSourceInstancePath(ds.dataSourceId)}
                    agentNames={agentNames}
                  />
                ))}
              {showConnectedMcp &&
                connectedMcps.map((mcp) => (
                  <CustomMcpCard
                    key={mcp.mcpId}
                    mcp={mcp}
                    to={mcpInstancePath(mcp.mcpId)}
                    agentNames={agentNames}
                  />
                ))}
            </Grid>
          )}
        </div>
      )}

      {hubTab === "catalog" && (
        <div style={{ marginTop: "1.25rem" }}>
          {category !== "tool" && showDataSourceCatalog && (
            <CatalogGroup>
              <CatalogGroupTitle>Data sources</CatalogGroupTitle>
              <Grid>
                {visibleDsCatalog.map((tile) => (
                  <CatalogTile
                    key={tile.type}
                    padding={16}
                    elevation="sm"
                  >
                    <CatalogTileHead>
                      <CatalogTileTitle>
                        {DS_TYPE_LABELS[tile.type]}
                      </CatalogTileTitle>
                      <Badge
                        variant={DS_TYPE_BADGE_VARIANT[tile.type] as any}
                        size="sm"
                      >
                        Available
                      </Badge>
                    </CatalogTileHead>
                    <CatalogTileDesc>{tile.description}</CatalogTileDesc>
                    <CatalogTileFoot>
                      <CatalogMeta>{tile.kind}</CatalogMeta>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => openWizard(tile.type)}
                      >
                        Create
                      </Button>
                    </CatalogTileFoot>
                  </CatalogTile>
                ))}
              </Grid>
            </CatalogGroup>
          )}

          {visibleTools.length > 0 && (
            <CatalogGroup>
              <CatalogGroupTitle>Tools</CatalogGroupTitle>
              <Grid>
                {category !== "data_source" && (
                  <CatalogTile
                    padding={16}
                    elevation="sm"
                    data-testid="custom-mcp-catalog-tile"
                  >
                    <CatalogTileHead>
                      <CatalogTileTitle>Custom MCP</CatalogTileTitle>
                      <Badge variant="success" size="sm">
                        Available
                      </Badge>
                    </CatalogTileHead>
                    <CatalogTileDesc>
                      Connect a remote HTTP MCP so agents can act in a partner
                      app as the signed-in user.
                    </CatalogTileDesc>
                    <CatalogTileFoot>
                      <CatalogMeta>Tool</CatalogMeta>
                      <Button variant="primary" size="sm" onClick={openMcpWizard}>
                        Create
                      </Button>
                    </CatalogTileFoot>
                  </CatalogTile>
                )}
                {visibleTools.map((tile) => (
                  <Link
                    key={tile.id}
                    to={tile.to}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <CatalogTile padding={16} elevation="sm">
                      <CatalogTileHead>
                        <CatalogTileTitle>{tile.name}</CatalogTileTitle>
                        <Badge variant="neutral" size="sm">
                          Coming soon
                        </Badge>
                      </CatalogTileHead>
                      <CatalogTileDesc>{tile.description}</CatalogTileDesc>
                      <CatalogTileFoot>
                        <CatalogMeta>Tool</CatalogMeta>
                        <CatalogMeta>Open →</CatalogMeta>
                      </CatalogTileFoot>
                    </CatalogTile>
                  </Link>
                ))}
              </Grid>
            </CatalogGroup>
          )}

          {visibleDsCatalog.length === 0 && visibleTools.length === 0 && (
            <EmptyState title="No catalog items match your search." />
          )}
        </div>
      )}

      <CreateWizard
        isOpen={wizardOpen}
        initialType={wizardType}
        onClose={() => {
          setWizardOpen(false);
          setWizardType(undefined);
          clearCreateParams();
        }}
        apiBaseUrl={API_BASE_URL}
        onOpenDetail={async (ds, tab) => {
          setWizardOpen(false);
          clearCreateParams();
          const returned = await navigateBackToAgent(ds);
          if (!returned) {
            navigate(dataSourceInstancePath(ds.dataSourceId, tab));
          }
        }}
      />

      <CustomMcpWizard
        isOpen={mcpWizardOpen}
        onClose={() => {
          setMcpWizardOpen(false);
          clearCreateParams();
        }}
        onOpenDetail={(mcp) => {
          setMcpWizardOpen(false);
          clearCreateParams();
          if (returnTo) {
            navigate(
              returnTab && !returnTo.includes("tab=")
                ? `${returnTo}${returnTo.includes("?") ? "&" : "?"}tab=${encodeURIComponent(returnTab)}`
                : returnTo,
            );
            return;
          }
          navigate(mcpInstancePath(mcp.mcpId));
        }}
      />
    </PageWrapper>
  );
};

export default IntegrationsHub;
